

import React, { useState, useEffect, useRef, Suspense, lazy } from 'react';
import NeuralBackground from './components/NeuralBackground';
import InputForm from './components/InputForm';
const ReactMarkdown = lazy(() => import('react-markdown'));

const API_URL = process.env.REACT_APP_API_URL || 'https://ashishmehra-nexus-backend.hf.space';

const cardStyle = {
  background: 'rgba(26,31,58,0.25)',
  backdropFilter: 'blur(2px)',
  border: '1px solid rgba(255,255,255,0.08)',
  boxShadow: 'none',
  borderRadius: '24px',
  padding: '16px',
  fontFamily: "'Plus Jakarta Sans', sans-serif",
};

const textareaStyle = {
  width: '100%',
  background: 'rgba(255,255,255,0.08)',
  border: '1px solid rgba(255,255,255,0.2)',
  borderRadius: '12px',
  padding: '12px',
  color: 'white',
  resize: 'vertical',
  fontFamily: 'inherit'
};

const TECHNICAL_DOMAINS = ['engineering', 'manufacturing', 'automotive', 'construction', 'technical', 'quality', 'production', 'machine', 'equipment', 'process', 'safety', 'maintenance'];
const BEHAVIORAL_DOMAINS = ['business', 'sales', 'marketing', 'leadership', 'management', 'communication', 'finance', 'legal', 'education', 'training', 'strategy', 'hr', 'planning', 'team', 'coaching'];

function detectDomain(topic) {
  const lower = topic.toLowerCase();
  if (TECHNICAL_DOMAINS.some(d => lower.includes(d))) return 'technical';
  if (BEHAVIORAL_DOMAINS.some(d => lower.includes(d))) return 'behavioral';
  return 'behavioral';
}

const PROGRESS_STEPS = [
  { time: 0,  msg: '🔍 Detecting domain...' },
  { time: 4,  msg: '🏢 Researching company & industry...' },
  { time: 10, msg: '📚 Gathering authoritative sources...' },
  { time: 18, msg: '⚙️ Building training framework...' },
  { time: 26, msg: '✍️ Writing Module 1 content...' },
  { time: 34, msg: '✍️ Writing Module 2 content...' },
  { time: 42, msg: '✍️ Writing Module 3 content...' },
  { time: 50, msg: '🎤 Creating facilitator guide...' },
  { time: 58, msg: '📝 Building participant handout...' },
  { time: 65, msg: '✅ Finalizing your training program...' },
];

const SAMPLE_CONTENT = `# SAMPLE: Strategic Planning Training
**Format:** Training | **Audience:** Executive/C-Suite | **Duration:** 1 Day

---

## MODULE 1: Blue Ocean Strategy — Creating Uncontested Market Space

### The Framework: Blue Ocean Strategy (Kim & Mauborgne, INSEAD)

**Core Insight:** Most organizations compete in "red oceans" — known markets with defined rules where competition is fierce. Blue Ocean Strategy shows you how to create "blue oceans" — uncontested market space where competition is irrelevant.

**The 4 Actions Framework (ERRC Grid):**
- **Eliminate:** Which factors the industry takes for granted should be eliminated?
- **Reduce:** Which factors should be reduced well below the industry standard?
- **Raise:** Which factors should be raised well above the industry standard?
- **Create:** Which factors should be created that the industry has never offered?

### Practice Exercise (35 min)

**What You Will Create:** Your organization's Strategic Canvas showing current position vs. 2 competitors.

**Instructions:**
1. List 8-10 competition factors in your industry
2. Rate yourself and 2 competitors on each factor (1-10 scale)
3. Apply ERRC Grid — be bold!
4. Write your positioning statement: "We are the only _____ that _____"

### Resources to Go Deeper
- [Blue Ocean Strategy - Harvard Business Review](https://hbr.org/search?term=blue+ocean+strategy)
- [McKinsey - Strategic Planning Insights](https://www.mckinsey.com/capabilities/strategy-and-corporate-finance/our-insights)
- [BCG - Strategy Resources](https://www.bcg.com/capabilities/strategy)

---

## 30-Day Implementation Plan

**Week 1:** Complete Strategic Canvas with leadership team
**Week 2:** Develop strategic vision and communication cascade
**Week 3:** Create strategic initiatives with clear owners and metrics
**Week 4:** Launch communication plan, establish monitoring cadence

## Success Metrics
- Leadership alignment score: 85%
- 75% of employees can articulate strategy in one sentence
- Strategic decision-making speed improves by 40%

---
*This is an abbreviated sample. Your generated training will include ALL modules with elaborate frameworks, templates, and curated video resources.*`;

const HOW_TO_CONTENT = `# How to Use NEXUS

---

**💬 I learn with your feedback which is important to serve you better. Please spare a few moments to fill a small questionnaire in the Feedback tab.**

---

## 🚀 Quick Start Guide

### Step 1: Enter Your Topic
Enter any professional topic in the "What's Your Training Topic?" field.

**Examples:**
- Strategic Planning
- Leadership Coaching
- Python Programming
- Ophthalmology for Residents
- Car Engine Diagnostics
- Tensile Testing Procedures

### Step 2: Add Company Details (Optional)
- **Company Name**: Enter the company receiving this training
- **Company Context**: Add specific details about their situation
- More detail = more customized training!

### Step 3: Select Settings
- **Audience Level**: Executive, Manager, Emerging Leader, or Individual
- **Format**: Training, Workshop, or Action Learning
- **Duration**: Half Day, 1 Day, or 2 Days
- **Delivery Mode**: In-Person, Virtual, or Hybrid

### Step 4: Click Generate
- Answer the context questions for better results
- Wait 60-90 seconds while NEXUS creates your content
- Click **Unlock Full Access** to view all materials

---

## 📦 What You'll Get

✅ Complete training content with frameworks and examples

✅ Facilitator guide with talking points

✅ Participant handout with note-taking space

✅ PowerPoint export ready for AI generation

---

## 💡 Pro Tips

1. Be specific with your topic
2. Add company context for hyper-customized content
3. Choose audience level carefully
4. Check the **Sample** tab to see example output

---

**Ready? Fill in your topic above and click Generate!**`;

function getPPTPrompt(topic) {
  return `Create a professional presentation on: ${topic || 'Training Topic'}

Please create comprehensive slides that:
- Start with a compelling title slide
- Include an overview of why this topic matters now
- Cover each module/section with clear explanations
- Use bullet points and visuals for frameworks
- Add real-world examples and case studies
- End with implementation steps and key takeaways

Make it executive-ready, visually engaging, and actionable.`;
}

const TABS = [
  { key: 'synopsis', label: '📖 Synopsis' },
  { key: 'content', label: '📄 Content' },
  { key: 'facilitator', label: '🎤 Facilitator' },
  { key: 'handout', label: '📝 Handout' },
  { key: 'ppt', label: '🎨 PPT Export' },
  { key: 'sample', label: '🎯 Sample' },
  { key: 'feedback', label: '💬 Feedback' },
  { key: 'faq', label: '❓ FAQ' },
  { key: 'about', label: '👤 About Creator' },
];

function EmailCaptureModal({ onSubmit, onClose }) {
  const [step, setStep] = useState('email'); // 'email' | 'otp'
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const inputRefs = useRef([]);

  // Countdown timer for resend
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  // Auto-focus first OTP box when step changes
  useEffect(() => {
    if (step === 'otp') {
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    }
  }, [step]);

  // ── Step 1: Send OTP ────────────────────────────────────────
  const handleSendOTP = async () => {
    if (!email || !email.includes('@') || !email.includes('.')) {
      setError('Please enter a valid email address');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/auth/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.message || 'Failed to send OTP. Please try again.');
        setLoading(false);
        return;
      }
      setStep('otp');
      setLoading(false);
    } catch (e) {
      setError('Failed to send OTP. Please try again.');
      setLoading(false);
    }
  };

  // ── OTP input handlers ──────────────────────────────────────
  const handleOtpChange = (index, value) => {
    // Allow only digits
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1); // only last digit if pasted multiple
    setOtp(newOtp);
    setError('');
    // Auto-advance
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
    // Auto-submit when all filled
    if (value && index === 5) {
      const fullCode = newOtp.join('');
      if (fullCode.length === 6) {
        // slight delay so state settles
        setTimeout(() => handleVerifyOTPDirect(newOtp), 100);
      }
    }
  };

  const handleVerifyOTP = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/auth/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email: email.trim().toLowerCase(), 
          otp: otp
        }),
      });
      const data = await res.json();
      if (data.success) {
        onSubmit(email.trim().toLowerCase());
      } else {
        setError(data.error || 'Invalid OTP. Please try again.');
      }
    } catch (e) {
      setError('Network error. Please try again.');
    }
    setLoading(false);
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'Enter') handleVerifyOTP();
  };

  // Handle paste on OTP boxes
  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    const newOtp = [...otp];
    pasted.split('').forEach((ch, i) => { newOtp[i] = ch; });
    setOtp(newOtp);
    const nextEmpty = newOtp.findIndex(v => !v);
    inputRefs.current[nextEmpty === -1 ? 5 : nextEmpty]?.focus();
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setOtp(['', '', '', '', '', '']);
    setError('');
    await handleSendOTP();
  };

  // ── Shared styles ───────────────────────────────────────────
  const overlayStyle = {
    position: 'fixed', inset: 0, zIndex: 100,
    background: 'rgba(0,0,0,0.88)', backdropFilter: 'blur(12px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
  };

  const cardStyle = {
    background: 'linear-gradient(135deg, rgba(26,31,58,0.98), rgba(45,27,78,0.98))',
    border: '1px solid rgba(102,126,234,0.4)',
    borderRadius: '24px', padding: '40px 36px',
    maxWidth: '440px', width: '100%',
    boxShadow: '0 30px 80px rgba(0,0,0,0.6)',
  };

  const inputStyle = {
    width: '100%', padding: '14px 16px', borderRadius: '12px',
    background: 'rgba(255,255,255,0.08)',
    border: error && step === 'email' ? '1px solid #ef4444' : '1px solid rgba(255,255,255,0.2)',
    color: 'white', fontSize: '1em', outline: 'none', boxSizing: 'border-box',
  };

  const primaryBtn = {
    width: '100%', padding: '14px', fontWeight: 700, color: 'white',
    borderRadius: '12px', border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
    fontSize: '1em',
    background: loading
      ? 'rgba(102,126,234,0.5)'
      : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    boxShadow: loading ? 'none' : '0 4px 20px rgba(102,126,234,0.4)',
    transition: 'all 0.2s',
    marginBottom: '16px',
  };

  // ── Render ──────────────────────────────────────────────────
  return (
    <div style={overlayStyle}>
      <div style={cardStyle}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '28px' }}>
          <div style={{ fontSize: '2.5em', marginBottom: '10px' }}>🧠</div>
          {step === 'email' ? (
            <>
              <h2 style={{ color: 'white', fontSize: '1.5em', fontWeight: 800, marginBottom: '8px' }}>
                Almost there!
              </h2>
              <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.95em', lineHeight: '1.6' }}>
                Enter your email to generate your{' '}
                <strong style={{ color: '#a78bfa' }}>FREE training program</strong>.
                We'll send you a one-time code.
              </p>
            </>
          ) : (
            <>
              <h2 style={{ color: 'white', fontSize: '1.5em', fontWeight: 800, marginBottom: '8px' }}>
                Check your email
              </h2>
              <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.95em', lineHeight: '1.6' }}>
                We sent a 6-digit code to{' '}
                <strong style={{ color: '#a78bfa' }}>{email}</strong>
              </p>
              <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.82em', marginTop: '4px' }}>
                Check your spam folder if you don't see it within 30 seconds.
              </p>
            </>
          )}
        </div>

        {/* Step 1: Email input */}
        {step === 'email' && (
          <>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.9em', fontWeight: 600, display: 'block', marginBottom: '8px' }}>
                Work Email Address
              </label>
              <input
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(''); }}
                onKeyPress={(e) => e.key === 'Enter' && handleSendOTP()}
                autoFocus
                style={inputStyle}
              />
              {error && <p style={{ color: '#ef4444', fontSize: '0.85em', marginTop: '6px' }}>{error}</p>}
            </div>

            <button onClick={handleSendOTP} disabled={loading} style={primaryBtn}>
              {loading ? '⏳ Sending code...' : '🚀 Send My Code'}
            </button>
          </>
        )}

        {/* Step 2: OTP boxes */}
        {step === 'otp' && (
          <>
            {/* 6 digit boxes */}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginBottom: '20px' }}>
              {otp.map((digit, i) => (
                <input
                  key={i}
                  ref={el => inputRefs.current[i] = el}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(i, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(i, e)}
                  onPaste={i === 0 ? handleOtpPaste : undefined}
                  style={{
                    width: '48px', height: '56px',
                    textAlign: 'center', fontSize: '1.5em', fontWeight: 700,
                    borderRadius: '12px',
                    background: digit ? 'rgba(102,126,234,0.25)' : 'rgba(255,255,255,0.08)',
                    border: error
                      ? '1px solid #ef4444'
                      : digit
                        ? '1px solid rgba(102,126,234,0.6)'
                        : '1px solid rgba(255,255,255,0.2)',
                    color: 'white', outline: 'none',
                    transition: 'all 0.15s',
                  }}
                />
              ))}
            </div>

            {error && (
              <p style={{ color: '#ef4444', fontSize: '0.85em', textAlign: 'center', marginBottom: '12px' }}>
                {error}
              </p>
            )}

            <button onClick={handleVerifyOTP} disabled={loading} style={primaryBtn}>
              {loading ? '⏳ Verifying...' : '✅ Verify & Continue'}
            </button>

            {/* Resend + change email */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <button
                onClick={handleResend}
                disabled={resendCooldown > 0 || loading}
                style={{
                  background: 'none', border: 'none', cursor: resendCooldown > 0 ? 'default' : 'pointer',
                  color: resendCooldown > 0 ? 'rgba(255,255,255,0.3)' : 'rgba(167,139,250,0.9)',
                  fontSize: '0.85em', padding: 0,
                }}
              >
                {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : '🔁 Resend code'}
              </button>

              <button
                onClick={() => { setStep('email'); setOtp(['','','','','','']); setError(''); }}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'rgba(255,255,255,0.4)', fontSize: '0.85em', padding: 0,
                }}
              >
                ← Change email
              </button>
            </div>
          </>
        )}

        {/* Footer */}
        <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.35)', fontSize: '0.8em', marginBottom: '12px' }}>
          🔒 We never spam. Your email is only used to manage your access.
        </div>

        <button onClick={onClose} style={{
          display: 'block', margin: '0 auto', background: 'none', border: 'none',
          color: 'rgba(255,255,255,0.35)', cursor: 'pointer', fontSize: '0.85em',
        }}>Cancel</button>

      </div>
    </div>
  );
}
// ─── PACKAGES SCREEN ─────────────────────────────────────────
const PACKAGES = [
  { key: 'single',   name: '🥉 Starter',      price: 15000,  generations: '1 Program',   validity: '30 days', perProgram: 15000 },
  { key: 'silver',   name: '🥈 Silver Pack',   price: 62500,  generations: '5 Programs',  validity: '30 days', perProgram: 12500 },
  { key: 'gold',     name: '🥇 Gold Pack',     price: 100000, generations: '10 Programs', validity: '30 days', perProgram: 10000, best: true },
  { key: 'platinum', name: '💎 Platinum Pack', price: 187500, generations: '25 Programs', validity: '90 days', perProgram: 7500 },
];

function PackagesScreen({ userEmail, onBack }) {
  const [loading, setLoading] = useState(null);

  // Load Razorpay script dynamically
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
    return () => document.body.removeChild(script);
  }, []);

  const handleBuyNow = async (pkg) => {
    if (!userEmail) { alert('Please login first!'); return; }
    setLoading(pkg.key);
    try {
      const res = await fetch(`${API_URL}/api/razorpay/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ package: pkg.key, email: userEmail }),
      });
      const order = await res.json();
      if (!order.success) throw new Error(order.error);

      const options = {
        key: order.keyId,
        amount: order.amount,
        currency: 'INR',
        name: 'NEXUS',
        description: pkg.name,
        order_id: order.orderId,
        prefill: { email: userEmail },
        theme: { color: '#667eea' },
        handler: async (response) => {
          const verifyRes = await fetch(`${API_URL}/api/razorpay/verify-payment`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
              email: userEmail,
              package: pkg.key,
            }),
          });
          const verifyData = await verifyRes.json();
          if (verifyData.success) {
            alert(`✅ Payment successful! ${pkg.name} activated.`);
            onBack();
          } else {
            alert('❌ Payment verification failed. Contact support.');
          }
        },
        modal: { ondismiss: () => setLoading(null) }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
    setLoading(null);
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #1a1f3a 0%, #2d1b4e 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div style={{ maxWidth: '960px', width: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <h1 style={{ color: 'white', fontSize: '2.2em', fontWeight: 800, marginBottom: '8px' }}>🧠 NEXUS</h1>
          <h2 style={{ color: 'white', fontSize: '1.4em', marginBottom: '8px' }}>Choose a Package to Continue</h2>
          <div style={{ textAlign: 'center', marginBottom: '16px' }}>
            <span style={{
              color: '#ffd700',
              fontSize: '1.6em',
              fontWeight: 900,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              textShadow: '0 0 20px rgba(255,215,0,0.4)'
            }}>
              🎉 Inaugural Pricing Limited Period
            </span>
          </div>
          <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '1em' }}>
            You've used your free generation{userEmail ? ` (${userEmail})` : ''}. Select a package to generate more programs.
          </p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '20px', marginBottom: '36px' }}>
          {PACKAGES.map(pkg => (
            <div key={pkg.key}
              onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-4px)'}
              onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
              style={{
                background: pkg.best ? 'rgba(102,126,234,0.25)' : 'rgba(255,255,255,0.05)',
                border: pkg.best ? '2px solid #667eea' : '1px solid rgba(255,255,255,0.15)',
                borderRadius: '18px', padding: '28px 22px',
                position: 'relative', backdropFilter: 'blur(10px)', transition: 'transform 0.2s',
              }}
            >
              {pkg.best && (
                <div style={{
                  position: 'absolute', top: '-13px', left: '50%', transform: 'translateX(-50%)',
                  background: 'linear-gradient(135deg, #ffd700, #ffa500)',
                  color: '#333', padding: '4px 14px', borderRadius: '12px',
                  fontSize: '0.75em', fontWeight: 700, whiteSpace: 'nowrap'
                }}>⭐ BEST VALUE</div>
              )}
              <div style={{ color: 'white', fontWeight: 700, fontSize: '1.1em', marginBottom: '14px' }}>{pkg.name}</div>
              <div style={{ marginBottom: '18px' }}>
                <div style={{ marginBottom: '4px' }}>
                  <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.9em', textDecoration: 'line-through', marginRight: '8px' }}>
                    ₹{pkg.price.toLocaleString('en-IN')}
                  </span>
                  <span style={{ background: 'rgba(255,215,0,0.2)', color: '#ffd700', fontSize: '0.7em', fontWeight: 700, padding: '2px 8px', borderRadius: '10px' }}>
                    50% OFF
                  </span>
                </div>
                <div>
                  <span style={{ color: '#a78bfa', fontSize: '0.9em' }}>₹</span>
                  <span style={{ color: '#a78bfa', fontSize: '2em', fontWeight: 800 }}>{Math.round(pkg.price * 0.5).toLocaleString('en-IN')}</span>
                </div>
              </div>
              <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.88em', lineHeight: '2' }}>
                <div>⚡ {pkg.generations} generations</div>
                <div>📅 Valid {pkg.validity}</div>
                <div>💰 ₹{pkg.perProgram.toLocaleString('en-IN')} / program</div>
              </div>
              <button
                onClick={() => handleBuyNow(pkg)}
                disabled={loading === pkg.key}
                style={{
                  marginTop: '20px', width: '100%', padding: '10px',
                  background: pkg.best ? 'linear-gradient(135deg, #667eea, #764ba2)' : 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.3)', borderRadius: '10px',
                  color: 'white', fontWeight: 700,
                  cursor: loading === pkg.key ? 'not-allowed' : 'pointer',
                  fontSize: '0.9em', opacity: loading === pkg.key ? 0.6 : 1
                }}>
                {loading === pkg.key ? '⏳ Loading...' : 'Buy Now →'}
              </button>
            </div>
          ))}
        </div>
        <div style={{ textAlign: 'center' }}>
          <button onClick={onBack} style={{
            background: 'transparent', border: '1px solid rgba(255,255,255,0.3)',
            color: 'rgba(255,255,255,0.7)', padding: '10px 28px', borderRadius: '8px',
            cursor: 'pointer', fontSize: '0.9em'
          }}>← Back to App</button>
        </div>
      </div>
    </div>
  );
}

// ─── MAIN APP ─────────────────────────────────────────────────
function App() {
  const [generatedContent, setGeneratedContent] = useState(null);
  const [activeTab, setActiveTab] = useState('synopsis');
  const [isGenerating, setIsGenerating] = useState(false);
  const [progressMsg, setProgressMsg] = useState('');
  const [elapsedTime, setElapsedTime] = useState(0);
  const [showQuestions, setShowQuestions] = useState(false);
  const [pendingFormData, setPendingFormData] = useState(null);
  const [detectedDomain, setDetectedDomain] = useState('behavioral');
  const [pptCopied, setPptCopied] = useState(false);

  // ── Screen: 'app' | 'packages'
  const [screen, setScreen] = useState('app');

  // ── Email capture modal
  const [showEmailCapture, setShowEmailCapture] = useState(false);
  const [pendingGenerateArgs, setPendingGenerateArgs] = useState(null);

  // ── User email (captured at generation time)
 const [userEmail, setUserEmail] = useState(() => localStorage.getItem('nexusEmail') || '');

// ── Handle Razorpay redirect back after payment ─────────────
useEffect(() => {
  const params = new URLSearchParams(window.location.search);
  if (params.get('payment') === 'success') {
    window.history.replaceState({}, '', '/');
    // Read directly from localStorage since userEmail state may not be set yet
    const email = localStorage.getItem('nexusEmail');
    if (email) {
      alert('✅ Payment successful! Your package has been activated. Your new generations are ready.');
    }
    setScreen('app');
  }
}, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [answers, setAnswers] = useState({ challenges: '', technical: '', behavioral: '', outcomes: '' });
  const [feedback, setFeedback] = useState({ rating: 5, whatWorked: '', whatNeedsImprovement: '', suggestions: '', wouldUseAgain: true });
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [showIdlePopup, setShowIdlePopup] = useState(false);
  const idleTimerRef = useRef(null);

  // ── Dynamic page titles based on active tab
  useEffect(() => {
    const titles = {
      synopsis: 'NEXUS | AI-Powered Learning Design Generator',
      content:  'Training Content | NEXUS',
      facilitator: 'Facilitator Guide | NEXUS',
      handout: 'Participant Handout | NEXUS',
      ppt: 'PPT Export | NEXUS',
      sample: 'Sample Output | NEXUS',
      feedback: 'Share Feedback | NEXUS',
      faq: 'FAQ | NEXUS',
      about: 'About the Creator | NEXUS',
    };
    document.title = titles[activeTab] || 'NEXUS | AI Training Generator';
  }, [activeTab]);
  
  useEffect(() => {
    if (!isGenerating) return;
    setElapsedTime(0);
    setProgressMsg(PROGRESS_STEPS[0].msg);
    const timer = setInterval(() => {
      setElapsedTime(prev => {
        const next = prev + 1;
        const step = [...PROGRESS_STEPS].reverse().find(s => next >= s.time);
        if (step) setProgressMsg(step.msg);
        return next;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [isGenerating]);
    useEffect(() => {
    const resetTimer = () => {
        if (showIdlePopup) return;
        clearTimeout(idleTimerRef.current);
        idleTimerRef.current = setTimeout(() => {
        setShowIdlePopup(true);
      }, 30000);
    };
    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    events.forEach(e => document.addEventListener(e, resetTimer, true));
    resetTimer();
    return () => {
      clearTimeout(idleTimerRef.current);
      events.forEach(e => document.removeEventListener(e, resetTimer, true));
    };
  }, []);
  // ── When user submits email in capture modal ────────────────
  const handleEmailSubmit = (email) => {
    localStorage.setItem('nexusEmail', email);
    setUserEmail(email);
    setShowEmailCapture(false);
    // Now proceed with generation that was waiting
    if (pendingGenerateArgs) {
      generateContent(pendingGenerateArgs.formData, pendingGenerateArgs.extraAnswers, email);
      setPendingGenerateArgs(null);
    }
  };

  const handleUnlock = async () => {
  const sessionId = generatedContent?.sessionId || generatedContent?.session_id;
  if (!sessionId) { alert("No session ID found!"); return; }
  try {
    const response = await fetch(`${API_URL}/api/unlock/${sessionId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data = await response.json();
    setGeneratedContent({ 
      ...generatedContent, 
      content: data.content, 
      facilitator: data.facilitator, 
      handout: data.handout, 
      isLocked: false 
    });
  } catch (error) {
    alert(`Failed to unlock: ${error.message}`);
  }
};

  const openContentPopup = (content, title) => {
    const newWin = window.open('', '_blank', 'width=1400,height=900,scrollbars=yes');
    if (!newWin) { alert('Please allow popups for this site'); return; }
    newWin.document.write(`
      <!DOCTYPE html><html><head><title>${title}</title>
      <script src="https://cdn.jsdelivr.net/npm/marked/marked.min.js"></script>
      <style>
        body{font-family:Georgia,serif;max-width:900px;margin:40px auto;padding:20px 40px;color:#111;line-height:1.8;font-size:20px;background:#fff}
        h1{color:#1a1a2e;border-bottom:3px solid #667eea;padding-bottom:10px;font-size:2.4em}
        h2{color:#2d1b4e;border-bottom:1px solid #ddd;padding-bottom:6px;margin-top:30px;font-size:1.8em}
        h3{color:#333;margin-top:20px;font-size:1.4em}
        ul,ol{margin:10px 0;padding-left:25px}li{margin:6px 0}
        blockquote{border-left:4px solid #667eea;padding-left:15px;color:#555;font-style:italic;margin:20px 0}
        hr{border:none;border-top:2px solid #eee;margin:30px 0}p{margin:12px 0}
        a{color:#667eea;text-decoration:underline}
        .print-btn{position:fixed;top:20px;right:20px;padding:12px 24px;background:linear-gradient(135deg,#667eea,#764ba2);color:white;border:none;border-radius:8px;cursor:pointer;font-size:14px;font-weight:bold}
        @media print{.print-btn{display:none}}
      </style></head><body>
      <button class="print-btn" onclick="window.print()">🖨️ Print / Save PDF</button>
      <div id="content"></div>
     <script>
      window.onload = function() {
        if (typeof marked !== 'undefined') {
          document.getElementById('content').innerHTML = marked.parse(${JSON.stringify(content)});
        } else {
          document.getElementById('content').innerHTML = '<pre style="white-space:pre-wrap">' + ${JSON.stringify(content)} + '</pre>';
        }
      }
      </script>
      </body></html>
    `);
    newWin.document.close();
  };

  const closeQuestionsModal = () => { setShowQuestions(false); setPendingFormData(null); };

  const handleFormSubmit = (formData) => {
    const domain = detectDomain(formData.topic);
    setDetectedDomain(domain);
    setPendingFormData(formData);
    setAnswers({ challenges: '', technical: '', behavioral: '', outcomes: '' });
    setShowQuestions(true);
  };

  // ── Core generation function ────────────────────────────────
  // emailOverride is passed when called right after email capture
  const generateContent = async (formData, extraAnswers, emailOverride) => {
    const email = emailOverride || userEmail;

    // If no email yet → show email capture modal, store args for after
    if (!email || email.trim() === '') {
      setPendingGenerateArgs({ formData, extraAnswers });
      setShowQuestions(false);
      setShowEmailCapture(true);
      return;
    }

    const startTime = Date.now();
    setIsGenerating(true);
    setGeneratedContent(null);

    try {
      let enhancedContext = formData.companyContext || '';
      if (extraAnswers.challenges?.trim()) {
        enhancedContext += `\n\n${'='.repeat(70)}\nMANDATORY: USER-SPECIFIED REQUIREMENTS\n${'='.repeat(70)}\n`;
        enhancedContext += `\n**CHALLENGE TO SOLVE:**\n${extraAnswers.challenges}\n`;
        enhancedContext += `→ MODULE 1 MUST start by addressing this exact challenge\n`;
        enhancedContext += `→ Framework in Module 1 MUST solve this (not generic)\n`;
        enhancedContext += `→ Exercise in Module 1 MUST use this as the scenario\n`;
      }
      if (extraAnswers.technical?.trim()) {
        enhancedContext += `\n**TECHNICAL CONTEXT (MUST USE IN EXAMPLES):**\n${extraAnswers.technical}\n`;
        enhancedContext += `→ Reference SPECIFIC equipment models mentioned above\n`;
        enhancedContext += `→ Use ACTUAL SOP numbers in procedures\n`;
        enhancedContext += `→ Apply REAL metrics/targets in examples\n`;
      }
      if (extraAnswers.behavioral?.trim()) {
        enhancedContext += `\n**CULTURAL CONTEXT (MUST USE IN SCENARIOS):**\n${extraAnswers.behavioral}\n`;
        enhancedContext += `→ Start Module 1 with the ACTUAL dysfunction described\n`;
        enhancedContext += `→ Role-play exercises MUST use this culture dynamic\n`;
        enhancedContext += `→ Avoid generic advice - address THIS specific culture\n`;
      }
      if (extraAnswers.outcomes?.trim()) {
        enhancedContext += `\n**SUCCESS CRITERIA (MUST BE MODULE OUTCOMES):**\n${extraAnswers.outcomes}\n`;
        enhancedContext += `→ Each module outcome MUST connect to these results\n`;
        enhancedContext += `→ Exercises MUST produce deliverables that achieve these\n`;
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 300000);

      const response = await fetch(`${API_URL}/api/generate`, {
  method: 'POST',
  headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
  signal: controller.signal,
  body: JSON.stringify({
    topic: formData.topic,
    companyName: formData.companyName,
    companyContext: enhancedContext.trim(),
    audienceLevel: formData.audience,
    format: formData.format,
    duration: formData.duration,
    deliveryMode: formData.deliveryMode,
    email: email,
  }),
});

      clearTimeout(timeoutId);

      // Free generation used up → go to packages
      if (response.status === 403) {
        setIsGenerating(false);
        setScreen('packages');
        return;
      }

      if (!response.ok) throw new Error('Generation failed');

      const data = JSON.parse(await response.text());
      setGeneratedContent({ ...data, isLocked: true, timeTaken: ((Date.now() - startTime) / 1000).toFixed(2) });
      setActiveTab('synopsis');
      setIsGenerating(false);
    } catch (error) {
      setIsGenerating(false);
      if (error.name === 'AbortError') {
        alert(`Timed out after ${Math.round((Date.now() - startTime) / 1000)}s`);
      } else {
        alert(`Error: ${error.message}`);
      }
    }
  };

  const handleSubmitFeedback = async () => {
    if (!generatedContent) { alert('Please generate a training program first!'); return; }
    try {
      const response = await fetch(`${API_URL}/api/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          generationId: generatedContent.generationId || generatedContent.session_id,
          topic: pendingFormData?.topic || 'Unknown',
          companyName: pendingFormData?.companyName || '',
          rating: feedback.rating,
          whatWorked: feedback.whatWorked,
          whatNeedsImprovement: feedback.whatNeedsImprovement,
          suggestions: feedback.suggestions,
          wouldUseAgain: feedback.wouldUseAgain
        }),
      });
      if (!response.ok) throw new Error('Failed to submit feedback');
      setFeedbackSubmitted(true);
      setTimeout(() => {
        setFeedback({ rating: 5, whatWorked: '', whatNeedsImprovement: '', suggestions: '', wouldUseAgain: true });
        setFeedbackSubmitted(false);
      }, 3000);
    } catch (error) {
      alert(`Failed to submit feedback: ${error.message}`);
    }
  };

  const UnlockButton = () => (
    <div className="text-center py-10">
      <p className="text-white text-xl mb-4">🔒 Content is locked</p>
      <button onClick={handleUnlock} style={{
        padding: '14px 40px', fontWeight: 'bold', color: 'white', borderRadius: '12px',
        border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>🔓 Unlock Full Access</button>
    </div>
  );

 const OpenFullPageButton = ({ content, title }) => (
  <button
    onClick={() => openContentPopup(content, title)}
    style={{
      marginBottom: '16px',
      padding: '10px 20px',
      background: 'linear-gradient(135deg, #667eea, #764ba2)',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      fontWeight: 'bold',
      fontSize: '0.9em',
      display: 'block'
    }}
  >
    📄 Open Full Page (White Background)
  </button>
);

const markdownFallback = (
  <p style={{ color: 'rgba(255,255,255,0.65)' }}>Loading content...</p>
);

// ── Screen: Packages ────────────────────────────────────────
if (screen === 'packages') {
  return <PackagesScreen userEmail={userEmail} onBack={() => setScreen('app')} />;
}


  // ── Screen: Main App ────────────────────────────────────────
  return (
    <div className="relative" style={{ background: 'transparent', padding: '8px', height: '100vh', overflow: 'auto' }}>
      <NeuralBackground />

      {/* Email Capture Modal */}
      {showEmailCapture && (
        <EmailCaptureModal
          onSubmit={handleEmailSubmit}
          onClose={() => { setShowEmailCapture(false); setPendingGenerateArgs(null); }}
        />
      )}
      {showIdlePopup && (
        <div style={{
          position: 'fixed', bottom: '30px', right: '30px', zIndex: 9999,
          background: 'linear-gradient(135deg, rgba(26,31,58,0.98), rgba(45,27,78,0.98))',
          border: '1px solid rgba(102,126,234,0.4)', borderRadius: '16px',
          padding: '20px 24px', maxWidth: '300px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
          animation: 'slideUp 0.3s ease',
        }}>
          <style>{`@keyframes slideUp { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }`}</style>
          <p style={{ color: 'white', fontWeight: 700, fontSize: '0.95em', marginBottom: '6px' }}>
            🙏 Have you filled in the <strong>Feedback form?</strong>
          </p>
          <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.82em', marginBottom: '14px' }}>
            Your feedback helps make NEXUS better for everyone!
          </p>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={() => { setActiveTab('feedback'); setShowIdlePopup(false); }} style={{
              flex: 1, padding: '8px', background: 'linear-gradient(135deg, #667eea, #764ba2)',
              border: 'none', borderRadius: '8px', color: 'white',
              fontWeight: 700, fontSize: '0.82em', cursor: 'pointer'
            }}>Go to Feedback</button>
            <button onClick={() => setShowIdlePopup(false)} style={{
              flex: 1, padding: '8px', background: 'rgba(255,255,255,0.1)',
              border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px',
              color: 'rgba(255,255,255,0.7)', fontSize: '0.82em', cursor: 'pointer'
            }}>Dismiss</button>
          </div>
        </div>
      )}
      <div className="relative z-10 container mx-auto px-4" style={{ maxWidth: '1400px' }}>

        {/* Hero Section */}
        <div className="container mx-auto mb-3 relative overflow-hidden" style={{
          maxWidth: '1400px',
          background: 'rgba(26,31,58,0.2)',
          backdropFilter: 'blur(2px)',
          boxShadow: 'none', border: '1px solid rgba(255,255,255,0.06)',
        }}>
          <div className="text-center relative z-10">
            <h1 className="text-3xl font-black text-white mb-1" style={{ letterSpacing: '-2px' }}>🧠 NEXUS</h1>
            <div className="text-white font-semibold text-lg mb-3" style={{ opacity: 0.95 }}>A Strategic Synthesis Engine</div>
            <p className="text-sm text-white max-w-3xl mx-auto mb-3" style={{ opacity: 0.9, lineHeight: '1.6' }}>
              Amalgamation of  consulting-grade research and behavioral science. A decision-grade intelligence layer for leadership teams.
            </p>
            <div className="flex flex-wrap gap-2 justify-center">
              {['✨ Domain Agnostic', '⚡ 90-Second Generation', '🎓 Research-Backed', '🔄 Auto-Customized', '📊 Export Ready'].map(pill => (
                <span key={pill} className="px-3 py-1 rounded-full text-white font-semibold text-xs" style={{
                  background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.3)'
                }}>{pill}</span>
              ))}
            </div>

            {/* Why NEXUS is Unique */}
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', gap: '6px', marginTop: '10px' }}>
              <span className="px-3 py-1 rounded-full text-white font-semibold text-xs" style={{
                background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.3)', marginRight: '6px'
              }}>
                Why NEXUS is Unique
              </span>
              {[
                { label: 'Board-level Macro Justification', tip: 'Frames the business case at board/C-suite level' },
                { label: 'Org-specific Strategic Diagnosis', tip: 'Org-specific analysis of current state' },
                { label: 'Financial Risk Exposure', tip: 'Quantified cost of inaction modeling' },
                { label: 'System Redesign Blueprint', tip: 'Structured framework for transformation' },
                { label: 'Evidence-backed Recommendations', tip: 'Research-grounded recommendations' },
             ].map(({ label, tip }) => (
                <div key={label} style={{ position: 'relative' }} className="nexus-chip">
                  <span style={{ background: 'rgba(139,92,246,0.12)', border: '1px solid rgba(139,92,246,0.25)', color: 'rgba(255,255,255,0.7)', padding: '3px 10px', borderRadius: '20px', fontSize: '0.7rem', fontWeight: 700, cursor: 'default', display: 'inline-block' }}>{label}</span>
                  <div className="nexus-tooltip">{tip}</div>
                </div>
              ))}
            </div>
          </div>

          {/* User bar — only show if email captured */}
          {userEmail && (
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              marginTop: '12px', paddingTop: '12px', borderTop: '1px solid rgba(255,255,255,0.2)'
            }}>
              <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.85em' }}>
                👤 <strong>{userEmail}</strong>
              </span>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={() => setScreen('packages')} style={{
                  background: 'linear-gradient(135deg, #667eea, #764ba2)', border: 'none',
                  color: 'white', padding: '6px 16px', borderRadius: '6px',
                  cursor: 'pointer', fontSize: '0.85em', fontWeight: '600'
                }}>📦 View Packages</button>
                <button onClick={() => {
                  localStorage.removeItem('nexusEmail');
                  setUserEmail('');
                  setGeneratedContent(null);
                }} style={{
                  background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.4)',
                  color: 'white', padding: '6px 16px', borderRadius: '6px',
                  cursor: 'pointer', fontSize: '0.85em', fontWeight: '600'
                }}>Logout</button>
              </div>
            </div>
          )}
        </div>

        {/* Stats Banner */}
        <div className="container mx-auto flex justify-around flex-wrap gap-2 p-2 rounded-2xl mb-2" style={{
          maxWidth: '1400px', background: 'rgba(26,31,58,0.25)',
          boxShadow: '0 10px 40px rgba(0,0,0,0.1)'
        }}>
          {[['∞','Domain Agnostic'],['3-5','Modules'],['90s','Avg Time'],['100%','Custom']].map(([num, label]) => (
            <div key={label} className="text-center text-white">
              <div className="text-3xl font-black mb-0" style={{
                background: 'linear-gradient(135deg, #667eea, #f093fb)',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'
              }}>{num}</div>
              <div className="text-xs" style={{ color: 'rgba(255,255,255,0.7)' }}>{label}</div>
            </div>
          ))}
        </div>

        {/* Enhancement Questions Modal */}
        {showQuestions && (
          <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(10px)' }}>
            <div style={{ ...cardStyle, maxWidth: '650px', width: '92%', maxHeight: '90vh', overflowY: 'auto', position: 'relative' }}>
              <button onClick={closeQuestionsModal} style={{
                position: 'absolute', top: '15px', right: '20px',
                background: 'transparent', border: 'none', fontSize: '22px', cursor: 'pointer', color: 'white'
              }}>✕</button>
              <div className="text-center mb-4 p-3 rounded-2xl" style={{ background: 'linear-gradient(135deg, rgba(102,126,234,0.3), rgba(118,75,162,0.3))' }}>
                <h2 className="text-2xl font-bold text-white mb-1">✨ Quick Context Check</h2>
                <p style={{ color: 'rgba(255,255,255,0.8)' }}>Answer 2-3 quick questions to make your training hyper-specific</p>
                {detectedDomain !== 'general' && (
                  <div className="mt-2 inline-block px-3 py-1 rounded-full text-sm font-semibold" style={{
                    background: detectedDomain === 'technical' ? 'rgba(102,126,234,0.4)' : 'rgba(255,107,53,0.4)',
                    color: 'white', border: `1px solid ${detectedDomain === 'technical' ? 'rgba(102,126,234,0.6)' : 'rgba(255,107,53,0.6)'}`
                  }}>
                    {detectedDomain === 'technical' ? '⚙️ Technical Training Detected' : '🧠 Leadership/Behavioral Training Detected'}
                  </div>
                )}
              </div>
              <div className="space-y-3">
                <div>
                  <label className="text-white font-semibold block mb-1">🎯 What specific challenges should this training address?</label>
                  <p className="text-xs mb-2" style={{ color: 'rgba(255,255,255,0.5)' }}>Be specific about the ACTUAL problem</p>
                  <textarea value={answers.challenges} onChange={(e) => setAnswers({ ...answers, challenges: e.target.value })}
                    placeholder="e.g. 'Team leads don't give feedback', 'Scrap rate too high'" rows="2" style={textareaStyle} />
                </div>
                {detectedDomain === 'technical' && (
                  <div>
                    <div className="p-3 rounded-lg mb-2" style={{ background: 'rgba(102,126,234,0.15)', borderLeft: '4px solid #667eea' }}>
                      <span className="text-white font-semibold">⚙️ Add equipment/process details</span>
                    </div>
                    <label className="text-white font-semibold block mb-1">⚙️ Equipment, Standards & Metrics</label>
                    <textarea value={answers.technical} onChange={(e) => setAnswers({ ...answers, technical: e.target.value })}
                      placeholder="Equipment, SOP numbers, metrics" rows="3" style={textareaStyle} />
                  </div>
                )}
                {detectedDomain === 'behavioral' && (
                  <div>
                    <div className="p-3 rounded-lg mb-2" style={{ background: 'rgba(255,107,53,0.15)', borderLeft: '4px solid #ff6b35' }}>
                      <span className="text-white font-semibold">🧠 Add culture details</span>
                    </div>
                    <label className="text-white font-semibold block mb-1">🧠 Culture, Dynamics & Real Scenarios</label>
                    <textarea value={answers.behavioral} onChange={(e) => setAnswers({ ...answers, behavioral: e.target.value })}
                      placeholder="Culture dynamics, team issues" rows="3" style={textareaStyle} />
                  </div>
                )}
                <div>
                  <label className="text-white font-semibold block mb-1">🎯 What should participants DO differently?</label>
                  <textarea value={answers.outcomes} onChange={(e) => setAnswers({ ...answers, outcomes: e.target.value })}
                    placeholder="e.g. 'Run standups in <15 min', 'Reduce scrap to <5%'" rows="2" style={textareaStyle} />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button onClick={() => { setShowQuestions(false); generateContent(pendingFormData, answers); }}
                  style={{ flex: 1, padding: '14px', fontWeight: 'bold', color: 'white', borderRadius: '12px', border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                  🚀 Generate with Context
                </button>
                <button onClick={() => { setShowQuestions(false); generateContent(pendingFormData, { challenges: '', technical: '', behavioral: '', outcomes: '' }); }}
                  style={{ flex: 1, padding: '14px', fontWeight: 'bold', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.2)', cursor: 'pointer', background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)' }}>
                  ⏭️ Skip — Generate Now
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Main Content Grid */}
        <div className="container mx-auto grid grid-cols-1 lg:grid-cols-3 gap-3 mb-4" style={{ maxWidth: '1400px' }}>
          <div className="lg:col-span-2">
            <InputForm onGenerate={handleFormSubmit} isGenerating={isGenerating} />
          </div>
          <div style={cardStyle}>
            {isGenerating ? (
              <div>
                <div className="text-white font-bold text-lg mb-4">⚡ Generating Your Training...</div>
                <div className="mb-2" style={{ color: 'rgba(255,255,255,0.7)', fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: '0.9em' }}>{progressMsg}</div>
                <div className="mb-4">
                  <div className="flex justify-between text-sm mb-2" style={{ color: 'rgba(255,255,255,0.5)' }}>
                    <span>Progress</span><span>{elapsedTime}s elapsed</span>
                  </div>
                  <div className="w-full rounded-full h-2" style={{ background: 'rgba(255,255,255,0.1)' }}>
                    <div className="h-2 rounded-full transition-all duration-1000" style={{
                      width: `${Math.min((elapsedTime / 150) * 100, 95)}%`,
                      background: 'linear-gradient(135deg, #667eea, #764ba2)'
                    }}></div>
                  </div>
                </div>
                <div className="space-y-2">
                  {PROGRESS_STEPS.map((step, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm" style={{
                      color: elapsedTime >= step.time ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.25)'
                    }}>
                      <span>{elapsedTime >= step.time ? '✅' : '⏳'}</span>
                      <span>{step.msg}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : generatedContent ? (
              <div>
                {generatedContent?.isLocked && (
                  <div className="mb-4 text-center">
                    <button onClick={handleUnlock} style={{
                      width: '100%', padding: '14px', fontWeight: 'bold', color: 'white', borderRadius: '12px',
                      border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', fontSize: '1.1em'
                    }}>🔓 Unlock Full Access</button>
                  </div>
                )}
                <div className="text-white font-semibold text-lg mb-3">📊 Generation Complete!</div>
                <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: '0.85em', lineHeight: '1.8', color: 'rgba(255,255,255,0.85)' }}>
                  ✅ Training Generated!<br /><br />
                  📌 Topic: {pendingFormData?.topic}<br />
                  🎯 Format: {pendingFormData?.format}<br />
                  👥 Audience: {pendingFormData?.audience}<br />
                  ⏱️ Duration: {pendingFormData?.duration}<br />
                  🌍 Domain: {generatedContent.domain || 'Business'}<br />
                  ⚡ Time: {generatedContent.timeTaken}s<br /><br />
                  {generatedContent.isLocked ? '🔒 Click Unlock above' : '🔓 Content unlocked!'}<br /><br />
                  💬 <strong style={{ color: 'white' }}>Please share your feedback!</strong>
                </div>
              </div>
            ) : (
              <div>
                <div className="text-white font-semibold text-lg mb-3">📊 Generation Status</div>
                <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: '0.9em', lineHeight: '1.8', color: 'rgba(255,255,255,0.85)' }}>
                  ✨ Ready to Generate!<br /><br />
                  🌍 Supported Domains:<br />
                  • Business & Leadership<br />
                  • Medical & Healthcare<br />
                  • Engineering & Technical<br />
                  • IT & Software Development<br />
                  • Finance & Accounting<br />
                  • Legal & Compliance<br />
                  • Education & Training<br />
                  • Manufacturing<br />
                  • Sales & Marketing<br /><br />
                  📋 What You'll Get:<br />
                  ✓ Complete training content<br />
                  ✓ Facilitator guide<br />
                  ✓ Participant handouts<br />
                  ✓ PPT export ready<br /><br />
                  👉 Fill in your topic!
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="container mx-auto mb-4" style={{ maxWidth: '1400px' }}>
          <div className="grid grid-cols-4 gap-2 p-2 rounded-2xl mb-2" style={{
            background: 'rgba(26,31,58,0.25)', border: '1px solid rgba(255,255,255,0.06)', backdropFilter: 'blur(2px)'
          }}>
            {TABS.map((tab) => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                className="py-1 px-2 rounded-xl font-semibold text-xs transition-all"
                style={activeTab === tab.key ? {
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white', boxShadow: '0 4px 15px rgba(102,126,234,0.4)'
                } : { color: '#ffffff' }}
              >{tab.label}</button>
            ))}
          </div>

          <div style={{ ...cardStyle, padding: '16px', minHeight: '200px' }}>
            {activeTab === 'synopsis' && (
              <div className="prose prose-invert max-w-none overflow-auto"
                style={{ maxHeight: '250px', fontFamily: "'Inter', -apple-system, sans-serif", fontSize: '0.95rem', lineHeight: '1.6' }}>
                <style>{`
                  .synopsis-content h1{font-size:1.8em;font-weight:800;margin:0 0 0.5em 0;color:white}
                  .synopsis-content h2{font-size:1.3em;font-weight:700;margin:1em 0 0.5em 0;color:#a78bfa}
                  .synopsis-content h3{font-size:1.1em;font-weight:600;margin:0.8em 0 0.3em 0;color:rgba(255,255,255,0.9)}
                  .synopsis-content p{margin:0.5em 0;color:rgba(255,255,255,0.85)}
                  .synopsis-content strong{color:white;font-weight:600}
                  .synopsis-content ul{margin:0.5em 0;padding-left:1.2em}
                  .synopsis-content li{margin:0.3em 0;color:rgba(255,255,255,0.85)}
                  .synopsis-content hr{border:none;border-top:1px solid rgba(255,255,255,0.2);margin:1em 0}
                `}</style>
                <div className="synopsis-content">
                 <Suspense fallback={markdownFallback}>
                  <ReactMarkdown>{generatedContent ? generatedContent.synopsis : HOW_TO_CONTENT}</ReactMarkdown>
                </Suspense>

                </div>
              </div>
            )}

            {activeTab === 'content' && (
              <div className="prose prose-invert max-w-none overflow-auto max-h-[400px]">
                {!generatedContent ? <p style={{ color: 'rgba(255,255,255,0.5)' }}>Generate a training program to see content here.</p>
                  : generatedContent.isLocked ? <UnlockButton />
                  : <div><OpenFullPageButton content={generatedContent.content} title="Training Content" /><ReactMarkdown>{generatedContent.content}</ReactMarkdown></div>}
              </div>
            )}

            {activeTab === 'facilitator' && (
              <div className="prose prose-invert max-w-none overflow-auto max-h-[600px]">
                {!generatedContent ? <p style={{ color: 'rgba(255,255,255,0.5)' }}>Generate a training program first.</p>
                  : generatedContent.isLocked ? <UnlockButton />
                  : <div><OpenFullPageButton content={generatedContent.facilitator} title="Facilitator Guide" /><ReactMarkdown>{generatedContent.facilitator}</ReactMarkdown></div>}
              </div>
            )}

            {activeTab === 'handout' && (
              <div className="prose prose-invert max-w-none overflow-auto max-h-[600px]">
                {!generatedContent ? <p style={{ color: 'rgba(255,255,255,0.5)' }}>Generate a training program first.</p>
                  : generatedContent.isLocked ? <UnlockButton />
                  : <div><OpenFullPageButton content={generatedContent.handout} title="Participant Handout" /><ReactMarkdown>{generatedContent.handout}</ReactMarkdown></div>}
              </div>
            )}

            {activeTab === 'ppt' && (
              <div>
                {!generatedContent || generatedContent.isLocked ? (
                  <div className="text-center py-10">
                    <p className="text-white text-xl mb-2">🎨 PPT Export</p>
                    <p style={{ color: 'rgba(255,255,255,0.6)' }}>Generate and unlock your training program first.</p>
                    {generatedContent?.isLocked && <div className="mt-4"><UnlockButton /></div>}
                  </div>
                ) : (
                  <div>
                    <div className="prose prose-invert max-w-none mb-4">
                      <h2 className="text-white">🎨 Ready to Create Your Presentation!</h2>
                      <h3 className="text-white/80">Option 1: Manual Export</h3>
                      <p className="text-white/70">Copy content from the Content tab and paste into PowerPoint.</p>
                      <h3 className="text-white/80">Option 2: AI-Generated PPT (Recommended!)</h3>
                      <p className="text-white/70">Use Gamma AI or GenSpark AI to automatically create your presentation.</p>
                    </div>
                    <div className="text-white font-semibold mb-2">📋 AI Prompt for Gamma/GenSpark:</div>
                    <div style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', padding: '16px', marginBottom: '16px' }}>
                      <p style={{ color: 'rgba(255,255,255,0.8)', fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: '0.85em', whiteSpace: 'pre-wrap', margin: 0 }}>
                        {getPPTPrompt(pendingFormData?.topic)}
                      </p>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <button onClick={() => { navigator.clipboard.writeText(getPPTPrompt(pendingFormData?.topic)); setPptCopied(true); setTimeout(() => setPptCopied(false), 2000); }} style={{
                        flex: 1, padding: '12px', fontWeight: 'bold', color: 'white', borderRadius: '12px', border: 'none', cursor: 'pointer',
                        background: pptCopied ? 'rgba(34,197,94,0.8)' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                      }}>{pptCopied ? '✅ Copied!' : '📋 Copy AI Prompt'}</button>
                      <button onClick={() => window.open('https://gamma.app', '_blank')} style={{ flex: 1, padding: '12px', fontWeight: 'bold', color: 'white', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.2)', cursor: 'pointer', background: 'rgba(255,255,255,0.1)' }}>🎨 Open Gamma AI</button>
                      <button onClick={() => window.open('https://genspark.ai', '_blank')} style={{ flex: 1, padding: '12px', fontWeight: 'bold', color: 'white', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.2)', cursor: 'pointer', background: 'rgba(255,255,255,0.1)' }}>✨ Open GenSpark AI</button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'sample' && (
              <div className="prose prose-invert max-w-none overflow-auto max-h-[350px]">
                <ReactMarkdown>{SAMPLE_CONTENT}</ReactMarkdown>
              </div>
            )}

            {activeTab === 'feedback' && (
              <div>
                <div className="mb-4 p-4 rounded-2xl" style={{ background: 'linear-gradient(135deg, rgba(102,126,234,0.2), rgba(118,75,162,0.2))' }}>
                  <h2 className="text-2xl font-bold text-white mb-2">💬 Your Feedback Matters!</h2>
                  <p className="text-white/80" style={{ fontSize: '1.1em', fontWeight: '600' }}>Help us serve you better by sharing your experience with NEXUS</p>
                </div>
                {feedbackSubmitted ? (
                  <div className="text-center py-10">
                    <div className="text-6xl mb-4">🎉</div>
                    <h3 className="text-2xl text-white font-bold mb-2">Thank you for your feedback!</h3>
                    <p className="text-white/70">Your input helps make NEXUS smarter and more useful.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div>
                      <label className="text-white font-semibold block mb-2">⭐ Overall Rating</label>
                      <div className="flex gap-2">
                        {[1,2,3,4,5].map(star => (
                          <button key={star} onClick={() => setFeedback({...feedback, rating: star})}
                            style={{ fontSize: '2em', background: 'none', border: 'none', cursor: 'pointer', color: star <= feedback.rating ? '#fbbf24' : 'rgba(255,255,255,0.3)' }}>★</button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="text-white font-semibold block mb-2">✅ What worked well?</label>
                      <textarea value={feedback.whatWorked} onChange={(e) => setFeedback({...feedback, whatWorked: e.target.value})} placeholder="e.g., Frameworks were relevant..." rows="3" style={textareaStyle} />
                    </div>
                    <div>
                      <label className="text-white font-semibold block mb-2">🔧 What needs improvement?</label>
                      <textarea value={feedback.whatNeedsImprovement} onChange={(e) => setFeedback({...feedback, whatNeedsImprovement: e.target.value})} placeholder="e.g., Exercises could be more detailed..." rows="3" style={textareaStyle} />
                    </div>
                    <div>
                      <label className="text-white font-semibold block mb-2">💡 Any suggestions?</label>
                      <textarea value={feedback.suggestions} onChange={(e) => setFeedback({...feedback, suggestions: e.target.value})} placeholder="e.g., Add video recommendations..." rows="3" style={textareaStyle} />
                    </div>
                    <div>
                      <label className="text-white font-semibold block mb-2">🔄 Would you use NEXUS again?</label>
                      <div className="flex gap-4">
                        <button onClick={() => setFeedback({...feedback, wouldUseAgain: true})} style={{ flex: 1, padding: '12px', borderRadius: '12px', border: '2px solid', cursor: 'pointer', borderColor: feedback.wouldUseAgain ? '#10b981' : 'rgba(255,255,255,0.2)', background: feedback.wouldUseAgain ? 'rgba(16,185,129,0.2)' : 'transparent', color: 'white', fontWeight: 'bold' }}>✅ Yes</button>
                        <button onClick={() => setFeedback({...feedback, wouldUseAgain: false})} style={{ flex: 1, padding: '12px', borderRadius: '12px', border: '2px solid', cursor: 'pointer', borderColor: !feedback.wouldUseAgain ? '#ef4444' : 'rgba(255,255,255,0.2)', background: !feedback.wouldUseAgain ? 'rgba(239,68,68,0.2)' : 'transparent', color: 'white', fontWeight: 'bold' }}>❌ No</button>
                      </div>
                    </div>
                    <button onClick={handleSubmitFeedback} style={{ width: '100%', padding: '16px', fontWeight: 'bold', color: 'white', borderRadius: '12px', border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', fontSize: '1.1em' }}>
                      🚀 Submit Feedback
                    </button>
                  </div>
                )}
              </div>
            )}
            {activeTab === 'faq' && (
              <div className="prose prose-invert max-w-none overflow-auto max-h-[600px]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                <h2 style={{ color: 'white', fontSize: '1.4em', marginBottom: '20px' }}>❓ Frequently Asked Questions</h2>
            
                {[
                  {
                    section: '🚀 How NEXUS Works',
                    items: [
                      { q: 'What is NEXUS?', a: 'NEXUS is an AI-powered training program generator. Enter any professional topic, and NEXUS creates a complete, research-backed training program including modules, frameworks, facilitator guide, and participant handout — in under 2 minutes.' },
                      { q: 'What formats does NEXUS generate?', a: 'NEXUS generates three formats: Training (structured modules with frameworks and exercises), Workshop (interactive sessions with activities), and Action Learning (multi-week real-world challenge programs).' },
                      { q: 'What domains does NEXUS support?', a: 'NEXUS is domain-agnostic and supports Business, Leadership, Sales & Marketing, Finance, Legal, Medical & Healthcare, Engineering, IT & Software, Manufacturing, Automotive, Hospitality, Construction, and Education.' },
                      { q: 'How long does generation take?', a: 'Most programs generate in 60–120 seconds depending on format and duration selected. Half Day programs are faster, 2-Day programs take slightly longer.' },
                      { q: 'Can I customize the output for a specific company?', a: 'Yes. Enter the company name and context in the Company fields. NEXUS will research the company, tailor all examples, case studies, and frameworks specifically to that organization.' },
                    ]
                  },
                  {
                    section: '💰 Pricing & Packages',
                    items: [
                      { q: 'Do I get a free generation?', a: 'Yes. Every new email address gets 1 free program generation with no payment required.' },
                      { q: 'What happens after I use my free generation?', a: 'You will be prompted to purchase a package. Choose from Starter (1 program), Silver (5 programs), Gold (10 programs), or Platinum (25 programs).' },
                      { q: 'Are the prices shown inclusive of taxes?', a: 'Yes. All displayed prices are inclusive of applicable taxes.' },
                      { q: 'How long are packages valid?', a: 'Starter, Silver, and Gold packs are valid for 30 days from purchase. Platinum is valid for 90 days.' },
                      { q: 'Can I buy another package after mine expires?', a: 'Yes. You can purchase a new package at any time. Unused generations from an expired package are forfeited.' },
                      { q: 'Is the 50% inaugural pricing permanent?', a: 'No. The 50% discount is for a limited period only as part of the launch offer. Prices will return to full rate after the introductory period ends.' },
                    ]
                  },
                  {
                    section: '📚 Content Quality & Frameworks',
                    items: [
                      { q: 'Where does NEXUS get its frameworks from?', a: 'NEXUS draws from authoritative sources including Harvard Business Review, McKinsey & Company, BCG, MIT Sloan Management Review, IEEE, ASME, PubMed, and domain-specific journals. All frameworks are named, sourced, and evidence-backed.' },
                      { q: 'Can I use the generated content commercially?', a: 'Yes. Content generated by NEXUS is yours to use for your training, consulting, and coaching engagements.' },
                      { q: 'How accurate are the statistics cited?', a: 'NEXUS generates statistics based on its training data from reputable sources. We recommend verifying specific figures before presenting them in high-stakes executive settings.' },
                      { q: 'Will two people generating the same topic get the same output?', a: 'No. Each generation is unique. The AI produces different frameworks, examples, and case studies each time, customized to the inputs provided.' },
                      { q: 'Can I regenerate if I am not happy with the output?', a: 'Yes. Each generation uses one credit from your package. You can regenerate with different settings or more specific context for a better result.' },
                    ]
                  },
                  {
                    section: '🔒 Data Privacy & Email Usage',
                    items: [
                      { q: 'Why do you need my email?', a: 'Your email is used solely to manage your access and generation credits. We do not sell, share, or use your email for advertising.' },
                      { q: 'Will I receive marketing emails?', a: 'No. We do not send unsolicited marketing emails. You may receive a one-time OTP for login and an occasional product update if you opt in.' },
                      { q: 'Is my company context data stored?', a: 'Company name and context you enter is used only to generate your program and is not stored permanently or used to train the AI.' },
                      { q: 'How is my payment data handled?', a: 'All payment processing is handled by Razorpay. NEXUS never sees or stores your card details. Razorpay is PCI-DSS compliant.' },
                    ]
                  },
                  {
                    section: '↩️ Refund Policy',
                    items: [
                      { q: 'Can I get a refund?', a: 'Refunds are considered on a case-by-case basis within 7 days of purchase if no generations have been used. Once a generation has been consumed, refunds are not applicable for that credit.' },
                      { q: 'How do I request a refund?', a: 'Email ashish.mehra@interfaceinc.co.in with your registered email, payment ID, and reason. We will respond within 2 business days.' },
                      { q: 'What if the generation failed due to a technical error?', a: 'If a generation fails due to a server error on our side, the credit will be restored to your account. Contact us with the details.' },
                    ]
                  },
                  {
                    section: '🛠️ Technical Issues',
                    items: [
                      { q: 'The generation is taking too long — what should I do?', a: 'Generation can occasionally take up to 3 minutes during peak load. If it exceeds 5 minutes, refresh the page. Your credit will not be deducted if generation fails.' },
                      { q: 'I did not receive my OTP email.', a: 'Check your spam or junk folder. OTPs expire in 10 minutes. If still not received, try requesting a new OTP using the Resend button.' },
                      { q: 'The content tab is blank after unlocking.', a: 'This is usually a browser issue. Try refreshing the page. If the problem persists, contact support with your session details.' },
                      { q: 'How do I contact support?', a: 'Email ashish.mehra@interfaceinc.co.in or connect on LinkedIn at linkedin.com/in/asmehra. We typically respond within 24 hours.' },
                    ]
                  },
                ].map(({ section, items }) => (
                  <div key={section} style={{ marginBottom: '28px' }}>
                    <h3 style={{ color: '#a78bfa', fontSize: '1.05em', fontWeight: 800, marginBottom: '12px', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '6px' }}>{section}</h3>
                    {items.map(({ q, a }) => (
                      <details key={q} style={{ marginBottom: '10px', background: 'rgba(255,255,255,0.04)', borderRadius: '10px', padding: '12px 16px', border: '1px solid rgba(255,255,255,0.08)' }}>
                        <summary style={{ color: 'white', fontWeight: 600, fontSize: '0.92em', cursor: 'pointer', listStyle: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          {q} <span style={{ color: '#a78bfa', marginLeft: '8px' }}>＋</span>
                        </summary>
                        <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.88em', lineHeight: '1.7', marginTop: '10px' }}>{a}</p>
                      </details>
                    ))}
                  </div>
                ))}
              </div>
            )}
            {activeTab === 'about' && (
              <div className="prose prose-invert max-w-none overflow-auto max-h-[350px]">
                <h1 style={{ color: 'white' }}>About the Creator</h1>
                <h2 style={{ color: 'rgba(255,255,255,0.9)' }}>Your Professional Thought Partner</h2>
                <p style={{ color: 'rgba(255,255,255,0.8)' }}>
                  <strong style={{ color: 'white' }}>Ashish Mehra</strong> is an ICF Level 2 certified transformational coach and leadership trainer with 1,000+ hours of coaching experience, working with CEOs and senior leaders across India, Canada, Singapore, and Africa.
                </p>
                <h2 style={{ color: 'rgba(255,255,255,0.9)' }}>Credentials & Experience</h2>
                <ul style={{ color: 'rgba(255,255,255,0.8)' }}>
                  <li>INSEAD Alumnus</li>
                  <li>ICF Level 2 Certified Coach</li>
                  <li>3 decades of experience: Xerox, Airtel, Singtel, Hitachi</li>
                  <li>Trained by Centre for Creative Leadership</li>
                </ul>
                <h2 style={{ color: 'rgba(255,255,255,0.9)' }}>Why NEXUS?</h2>
                <p style={{ color: 'rgba(255,255,255,0.8)' }}>
                  After 5 years of manually creating training programs taking 40-80 hours each, I created NEXUS to combine my expertise with AI to generate complete, research-backed training programs instantly.
                </p>
                <h2 style={{ color: 'rgba(255,255,255,0.9)' }}>Connect With Me</h2>
                <p style={{ color: 'rgba(255,255,255,0.8)' }}>
                  📧 ashish.mehra@interfaceinc.co.in<br />
                  💼 <a href="https://www.linkedin.com/in/asmehra" target="_blank" rel="noreferrer" style={{ color: '#667eea' }}>linkedin.com/in/asmehra</a><br />
                  🌐 <a href="https://interfaceinc.co.in" target="_blank" rel="noreferrer" style={{ color: '#667eea' }}>interfaceinc.co.in</a>
                </p>
                <div style={{ marginTop: 24, fontSize: 12, opacity: 0.75 }}>
                  <a href="https://interfaceinc.co.in/nexus-1" target="_blank" rel="noopener noreferrer">NEXUS Program</a>
                  {" | "}
                  <a href="https://interfaceinc.co.in/contact-us" target="_blank" rel="noopener noreferrer">Contact Us</a>
                  {" | "}
                  <a href="https://interfaceinc.co.in/book-a-call" target="_blank" rel="noopener noreferrer">Book a Call</a>
                </div>

              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
